'use client';

import React from 'react';
import { RootState } from '@/store';
import { Box, CircularProgress, Stack, Typography, TypographyProps } from '@mui/material';
import { Clock } from '@phosphor-icons/react/dist/ssr/Clock';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';
import { useSelector } from 'react-redux';

// eslint-disable-next-line import/no-named-as-default-member
dayjs.extend(relativeTime);
// eslint-disable-next-line import/no-named-as-default-member
dayjs.extend(utc);
// eslint-disable-next-line import/no-named-as-default-member
dayjs.extend(timezone);

export function FormattedDateTime({
  timestamp,
  showRelativeTime = true,
  showDateTime = true,
  position,
  dateTimeProps,
  relativeTimeProps,
  format = 'YYYY-MM-DD HH:mm:ss',
  variant,
  overrideFormattedDate,
  isLoading,
}: {
  timestamp: Date | undefined;
  position?: 'center' | 'start' | 'end';
  showRelativeTime?: boolean;
  showDateTime?: boolean;
  dateTimeProps?: TypographyProps;
  relativeTimeProps?: TypographyProps;
  format?: string;
  variant?: 'vertical' | 'horizontal';
  overrideFormattedDate?: string;
  isLoading?: boolean;
}): React.JSX.Element | null {
  const { user } = useSelector((state: RootState) => state.user);
  if (!user || !timestamp) return null;

  const userTimezone = user?.timezone || dayjs.tz.guess();

  const formattedDateTime = dayjs(timestamp).tz(userTimezone).format(format);
  const userRelativeTime = dayjs(timestamp).tz(userTimezone).fromNow();

  return (
    <Stack
      sx={{
        flexDirection: variant === 'horizontal' ? 'row' : 'column',
        justifyContent: 'initial',
        position: 'relative',
        alignItems: position,
        gap: variant === 'horizontal' ? 0.5 : 0,
        whiteSpace: 'nowrap',
        px: 1,
        py: 0.5,
      }}
      className="formattedDateTime"
    >
      {isLoading && showDateTime ? (
        <Box sx={{ gap: 0.5, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
          <Clock size={14} />
          <Typography variant="caption" {...dateTimeProps}>
            Last update
          </Typography>
          <CircularProgress color="primary" size={14} />
        </Box>
      ) : null}
      {!isLoading && showDateTime ? (
        <Stack direction="row" sx={{ gap: 0.5, alignItems: 'center' }}>
          <Clock size={14} />
          <Typography variant="caption" {...dateTimeProps}>
            Last update: {overrideFormattedDate ?? formattedDateTime}
          </Typography>
          {showRelativeTime ? (
            <Typography variant="caption" color="text.secondary" {...relativeTimeProps}>
              {userRelativeTime}
            </Typography>
          ) : null}
        </Stack>
      ) : null}
    </Stack>
  );
}

export default FormattedDateTime;
